import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
import { Button } from '../../components/ui/Button';
import { FileTextIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
export interface EditCompanyTemplatesProps {
  invoiceEmailTemplate: string;
  setInvoiceEmailTemplate: (v: string) => void;
  invoiceEmailSubjectTemplate: string;
  setInvoiceEmailSubjectTemplate: (v: string) => void;
  invoiceSmsTemplate: string;
  setInvoiceSmsTemplate: (v: string) => void;
  otpEmailTemplate: string;
  setOtpEmailTemplate: (v: string) => void;
  otpEmailSubjectTemplate: string;
  setOtpEmailSubjectTemplate: (v: string) => void;
  companyErrors: Record<string, string>;
  isSavingCompany: boolean;
  handleSaveCompany: (overrides?: {
    invoiceEmailTemplate?: string;
    otpEmailTemplate?: string;
  }) => void;
  closeEditCompany: () => void;
  syncEditorContent: () => string;
  editorInitializedRef: React.MutableRefObject<boolean>;
}
const templateVariables = [{
  variable: '%invoice_link%',
  description: 'Link to view/pay invoice'
}, {
  variable: '%invoice_number%',
  description: 'Invoice number'
}, {
  variable: '%invoice_date%',
  description: 'Invoice date'
}, {
  variable: '%invoice_status%',
  description: 'Invoice status'
}, {
  variable: '%invoice_amount%',
  description: 'Invoice total amount'
}, {
  variable: '%customer_name%',
  description: 'Customer name'
}];
const otpTemplateVariables = [{
  variable: '%code%',
  description: 'One-time verification code'
}];
export function EditCompanyTemplates({
  invoiceEmailTemplate,
  setInvoiceEmailTemplate,
  invoiceEmailSubjectTemplate,
  setInvoiceEmailSubjectTemplate,
  invoiceSmsTemplate,
  setInvoiceSmsTemplate,
  otpEmailTemplate,
  setOtpEmailTemplate,
  otpEmailSubjectTemplate,
  setOtpEmailSubjectTemplate,
  companyErrors,
  isSavingCompany,
  handleSaveCompany,
  closeEditCompany,
  syncEditorContent,
  editorInitializedRef
}: EditCompanyTemplatesProps) {
  const emailEditorRef = useRef<HTMLDivElement>(null);
  const otpEditorRef = useRef<HTMLDivElement>(null);
  const otpEditorInitializedRef = useRef(false);
  const [linkTooltip, setLinkTooltip] = useState<{
    url: string;
    x: number;
    y: number;
  } | null>(null);
  // Initialize invoice editor content
  useEffect(() => {
    if (emailEditorRef.current && !editorInitializedRef.current) {
      emailEditorRef.current.innerHTML = invoiceEmailTemplate;
      editorInitializedRef.current = true;
    }
  }, [invoiceEmailTemplate, editorInitializedRef]);
  // Initialize OTP editor content
  useEffect(() => {
    if (otpEditorRef.current && !otpEditorInitializedRef.current) {
      otpEditorRef.current.innerHTML = otpEmailTemplate;
      otpEditorInitializedRef.current = true;
    }
  }, [otpEmailTemplate]);
  const localSyncEditorContent = useCallback(() => {
    if (emailEditorRef.current) {
      setInvoiceEmailTemplate(emailEditorRef.current.innerHTML);
    }
    if (otpEditorRef.current) {
      setOtpEmailTemplate(otpEditorRef.current.innerHTML);
    }
    return emailEditorRef.current?.innerHTML || invoiceEmailTemplate;
  }, [invoiceEmailTemplate, setInvoiceEmailTemplate, setOtpEmailTemplate]);
  const handleEditorMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    if (link) {
      const href = link.getAttribute('href');
      if (href) {
        const rect = link.getBoundingClientRect();
        setLinkTooltip({
          url: href,
          x: rect.left,
          y: rect.bottom + 4
        });
      }
    } else {
      setLinkTooltip(null);
    }
  }, []);
  const handleEditorMouseLeave = useCallback(() => {
    setLinkTooltip(null);
  }, []);
  const handleEditorPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text) document.execCommand('insertText', false, text);
  }, []);
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };
  const insertVariable = (variable: string) => {
    if (emailEditorRef.current) {
      emailEditorRef.current.focus();
      document.execCommand('insertText', false, variable);
    }
  };
  const insertOtpVariable = (variable: string) => {
    if (otpEditorRef.current) {
      otpEditorRef.current.focus();
      document.execCommand('insertText', false, variable);
    }
  };
  const insertSmsVariable = (variable: string) => {
    setInvoiceSmsTemplate(invoiceSmsTemplate + variable);
  };
  const handleSave = () => {
    // Read fresh content directly from editors before saving
    const freshInvoiceContent = emailEditorRef.current?.innerHTML || invoiceEmailTemplate;
    const freshOtpContent = otpEditorRef.current?.innerHTML || otpEmailTemplate;
    // Sync state for UI consistency
    localSyncEditorContent();
    // Pass fresh content directly to avoid stale state race condition
    handleSaveCompany({
      invoiceEmailTemplate: freshInvoiceContent,
      otpEmailTemplate: freshOtpContent
    });
  };
  // Reusable rich text toolbar
  const RichTextToolbar = ({
    variables,
    onInsertVariable
  }: {
    variables: {
      variable: string;
      description: string;
    }[];
    onInsertVariable: (v: string) => void;
  }) => <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 flex-wrap" data-id="element-2935">
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('bold')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Bold" data-id="element-2936">
        <span className="font-bold text-sm" data-id="element-2937">B</span>
      </button>
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('italic')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Italic" data-id="element-2938">
        <span className="italic text-sm" data-id="element-2939">I</span>
      </button>
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('underline')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Underline" data-id="element-2940">
        <span className="underline text-sm" data-id="element-2941">U</span>
      </button>
      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2942" />
      <div className="relative group" data-id="element-2943">
        <button type="button" className="px-2 py-1 rounded hover:bg-gray-200 transition-colors text-xs text-gray-600 flex items-center gap-1" title="Font Size" data-id="element-2944">
          <span className="text-sm" data-id="element-2945">A</span>
          <span className="text-[10px]" data-id="element-2946">▾</span>
        </button>
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[100px]" data-id="element-2947">
          {[{
          value: '1',
          label: 'Small'
        }, {
          value: '2',
          label: 'Normal'
        }, {
          value: '3',
          label: 'Medium'
        }, {
          value: '4',
          label: 'Large'
        }, {
          value: '5',
          label: 'X-Large'
        }, {
          value: '6',
          label: 'XX-Large'
        }].map(size => <button key={size.value} type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('fontSize', size.value)} className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg" data-id="element-2948">
              {size.label}
            </button>)}
        </div>
      </div>
      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2949" />
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => {
      const url = prompt('Enter link URL:');
      if (url) execCommand('createLink', url);
    }} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-sm" title="Insert Link" data-id="element-2950">
        🔗
      </button>
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('unlink')} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-sm text-red-500" title="Remove Link" data-id="element-2951">
        🔗✕
      </button>
      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2952" />
      <div className="relative group" data-id="element-2953">
        <button type="button" className="px-2 py-1 rounded hover:bg-gray-200 transition-colors text-xs text-gray-600" data-id="element-2954">
          Insert Variable ▾
        </button>
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[180px] max-h-[200px] overflow-y-auto" data-id="element-2955">
          {variables.map(v => <button key={v.variable} type="button" onMouseDown={e => e.preventDefault()} onClick={() => onInsertVariable(v.variable)} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg" data-id="element-2956">
              <code className="text-blue-600" data-id="element-2957">{v.variable}</code>
              <span className="block text-xs text-gray-500" data-id="element-2958">
                {v.description}
              </span>
            </button>)}
        </div>
      </div>
    </div>;
  return <div className="space-y-6" data-id="element-2959">
      {/* Email Template */}
      <Card data-id="element-2960">
        <div className="space-y-4" data-id="element-2961">
          <div className="flex items-center gap-2" data-id="element-2962">
            <MailIcon className="w-5 h-5 text-gray-400" data-id="element-2963" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2964">
              Invoice Email Template
            </h3>
          </div>
          <p className="text-sm text-gray-500" data-id="element-2965">
            This template will be used when sending invoices via email.
          </p>

          {/* Available Variables - scoped to invoice templates */}
          <div className="flex items-start gap-3 p-3 bg-blue-50/60 rounded-lg border border-blue-100" data-id="element-2966">
            <div className="p-1.5 bg-blue-100 rounded-md mt-0.5" data-id="element-2967">
              <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-2968" />
            </div>
            <div data-id="element-2969">
              <h4 className="text-xs font-medium text-gray-700 mb-1.5" data-id="element-2970">
                Available Variables
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-2971">
                {templateVariables.map(v => <code key={v.variable} className="px-1.5 py-0.5 bg-white text-gray-700 text-xs rounded font-mono border border-blue-100" title={v.description} data-id="element-2972">
                    {v.variable}
                  </code>)}
              </div>
            </div>
          </div>

          <Input label="Email Subject" value={invoiceEmailSubjectTemplate} onChange={e => setInvoiceEmailSubjectTemplate(e.target.value)} placeholder="e.g., Invoice #%invoice_number% from Your Company" helperText="You can use template variables in the subject line" data-id="element-2973" />
          <div data-id="element-2974">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2975">
              Email Body
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2976">
              <RichTextToolbar variables={templateVariables} onInsertVariable={insertVariable} data-id="element-2977" />
              <div ref={emailEditorRef} contentEditable className="min-h-[200px] p-4 focus:outline-none prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer" style={{
              whiteSpace: 'pre-wrap'
            }} onMouseMove={handleEditorMouseMove} onMouseLeave={handleEditorMouseLeave} onPaste={handleEditorPaste} suppressContentEditableWarning data-id="element-2978" />
            </div>
            {linkTooltip && <div className="fixed z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg max-w-xs truncate" style={{
            left: linkTooltip.x,
            top: linkTooltip.y
          }} data-id="element-2979">
                {linkTooltip.url}
              </div>}
          </div>
        </div>
      </Card>

      {/* SMS Template */}
      <Card data-id="element-2980">
        <div className="space-y-4" data-id="element-2981">
          <div className="flex items-center gap-2" data-id="element-2982">
            <MessageSquareIcon className="w-5 h-5 text-gray-400" data-id="element-2983" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2984">
              Invoice SMS Template
            </h3>
          </div>
          <p className="text-sm text-gray-500" data-id="element-2985">
            This template will be used when sending invoices via SMS. Keep it
            short and concise.
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-2986">
            {templateVariables.map(v => <button key={v.variable} type="button" onClick={() => insertSmsVariable(v.variable)} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded font-mono transition-colors" title={`Insert ${v.description}`} data-id="element-2987">
                + {v.variable}
              </button>)}
          </div>
          <TextArea value={invoiceSmsTemplate} onChange={e => setInvoiceSmsTemplate(e.target.value)} placeholder="Hi %customer_name%, your invoice #%invoice_number% for %invoice_amount% is ready. View it here: %invoice_link%" className="min-h-[120px] font-mono text-sm" data-id="element-2988" />
          <p className="text-xs text-gray-500" data-id="element-2989">
            Character count: {invoiceSmsTemplate.length} (SMS messages are
            typically limited to 160 characters)
          </p>
        </div>
      </Card>

      {/* OTP Email Template */}
      <Card data-id="element-2990">
        <div className="space-y-4" data-id="element-2991">
          <div className="flex items-center gap-2" data-id="element-2992">
            <MailIcon className="w-5 h-5 text-gray-400" data-id="element-2993" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2994">
              OTP Email Template
            </h3>
          </div>
          <p className="text-sm text-gray-500" data-id="element-2995">
            This template will be used when sending one-time password (OTP)
            verification emails.
          </p>

          {/* OTP Variable */}
          <div className="flex items-start gap-3 p-3 bg-blue-50/60 rounded-lg border border-blue-100" data-id="element-2996">
            <div className="p-1.5 bg-blue-100 rounded-md mt-0.5" data-id="element-2997">
              <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-2998" />
            </div>
            <div data-id="element-2999">
              <h4 className="text-xs font-medium text-gray-700 mb-1.5" data-id="element-3000">
                Available Variable
              </h4>
              <div className="flex flex-wrap gap-1.5" data-id="element-3001">
                {otpTemplateVariables.map(v => <code key={v.variable} className="px-1.5 py-0.5 bg-white text-gray-700 text-xs rounded font-mono border border-blue-100" title={v.description} data-id="element-3002">
                    {v.variable}
                  </code>)}
              </div>
            </div>
          </div>

          <Input label="OTP Email Subject" value={otpEmailSubjectTemplate} onChange={e => setOtpEmailSubjectTemplate(e.target.value)} placeholder="e.g., Your verification code" helperText="Subject line for OTP verification emails" data-id="element-3003" />

          <div data-id="element-3004">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3005">
              OTP Email Body
            </label>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-3006">
              <RichTextToolbar variables={otpTemplateVariables} onInsertVariable={insertOtpVariable} data-id="element-3007" />
              <div ref={otpEditorRef} contentEditable className="min-h-[200px] p-4 focus:outline-none prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer" style={{
              whiteSpace: 'pre-wrap'
            }} onMouseMove={handleEditorMouseMove} onMouseLeave={handleEditorMouseLeave} onPaste={handleEditorPaste} suppressContentEditableWarning data-id="element-3008" />
            </div>
          </div>
        </div>
      </Card>

      {companyErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-3009">
          <p className="text-sm text-red-600" data-id="element-3010">{companyErrors.submit}</p>
        </div>}

      <div className="flex justify-end gap-3" data-id="element-3011">
        <Button variant="secondary" onClick={closeEditCompany} disabled={isSavingCompany} data-id="element-3012">
          Cancel
        </Button>
        <Button onClick={handleSave} isLoading={isSavingCompany} data-id="element-3013">
          Save Changes
        </Button>
      </div>
    </div>;
}